import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { supabasePublic } from '@/integrations/supabase/publicClient';
import { TimeSlotDateOverride } from '@/lib/timeSlotUtils';
import { normalizeTimeForUI } from '@/lib/timeFormatUtils';

export function dateOverridesQueryKey(establishmentId: string | null) {
  return ['date-overrides', establishmentId] as const;
}

export function useDateOverridesQuery(
  establishmentId: string | null,
  useAuthenticatedData = false
) {
  return useQuery({
    queryKey: dateOverridesQueryKey(establishmentId),
    queryFn: async (): Promise<TimeSlotDateOverride[]> => {
      const client = useAuthenticatedData ? supabase : supabasePublic;
      let query = client
        .from('time_slot_date_overrides')
        .select('*')
        .eq('is_active', true)
        .order('date', { ascending: true })
        .order('start_time', { ascending: true });
      if (establishmentId) query = query.eq('establishment_id', establishmentId);

      const { data, error } = await query;
      if (error) {
        console.error('[useDateOverridesQuery] Error fetching date overrides:', error);
        return [];
      }

      return (data || []).map((o: any) => ({
        id: o.id,
        establishmentId: o.establishment_id,
        date: o.date,
        startTime: normalizeTimeForUI(o.start_time),
        endTime: normalizeTimeForUI(o.end_time),
        durationOverrideMinutes: o.duration_override_minutes ?? null,
        label: o.label || undefined,
        isActive: o.is_active,
      }));
    },
    enabled: Boolean(establishmentId),
    staleTime: 1000 * 60 * 2,
  });
}
